import { AlertOutlined, DownloadOutlined, FileTextOutlined, HistoryOutlined, WarningFilled } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Collapse, Empty, Input, Modal, Row, Statistic, Table, Tabs, Tag, Typography, message } from 'antd';
import { useState } from 'react';
import axios from '../config/axios';
import dayjs from '../config/dayjs';
import { handleAPIError } from '../utils/helpers';

const { Text, Title } = Typography;

interface AccountDetail {
	SNo?: number;
	Institution?: string;
	Client_Number?: string;
	Credit_Amount?: string;
	Monthly_Installment_Amount?: string;
	Credit_Account_Type?: string;
	Credit_Account_Date?: string;
	Maturity_Date?: string;
	Loan_Purpose?: string;
	Last_Transaction?: Array<{
		Transaction_Date?: string;
		Current_Balance_Amount?: string;
		Days_in_Arrears?: number;
		Last_Payment_Amount?: string;
		Last_Payment_Date?: string;
		Amount_Overdue?: string;
	}>;
	Amount_Overdue?: string;
}

interface ApplicationDetail {
	SNo?: number;
	Institution?: string;
	Product_Type?: string;
	Application_Date?: string;
	Amount?: string;
	Currency?: string;
	Application_Status?: string;
	Last_Status_Date?: string;
}

interface ScoreHistoryEntry {
	MonthYear: string;
	Score: string;
}

interface CreditReport {
	id: number;
	enquiry_id: string;
	individual_id: number;
	sector: string;
	match_metadata: Record<string, unknown>;
	customer_name: string;
	current_score: number | null;
	score_band: string;
	score_rating: string;
	probability_of_default: string;
	likelihood_to_default: string;
	evaluation_period: string;
	open_accounts: number | null;
	closed_accounts: number | null;
	adverse_accounts: number | null;
	worst_days_in_arrears: number | null;
	total_loan_balance: number | null;
	total_amount_overdue: number | null;
	raw_report: {
		credit_information?: {
			identification_details?: Array<Record<string, unknown>>;
			customer_details?: Record<string, unknown>;
			employment_history?: Array<{ Employer_Name?: string; Occupation?: string; Income_Band?: string }>;
			physical_address_history?: Array<{ Address?: string; Period_at_Address?: string; Ownership?: string }>;
			scoring_details?: {
				Score_History?: ScoreHistoryEntry[];
				Score_Explanation?: string[];
			};
			credit_applications_summary?: Array<Record<string, unknown>>;
			credit_accounts_summary?: Array<Record<string, unknown>>;
			credits_accounts_classification_summary?: Array<Record<string, unknown>>;
			collateral_guarantor_summary?: Array<Record<string, unknown>>;
			bounced_cheques_summary?: Array<Record<string, unknown>>;
			financial_fraud_summary?: Array<Record<string, unknown>>;
			credit_applications_details?: ApplicationDetail[];
			credit_account_details?: AccountDetail[];
		};
	};
	created_at: string;
}

interface CreditEnquiry {
	id: number;
	enquiry_id: string;
	identifier: string;
	reason: string;
	status: string;
	matches_count: number;
	run_by: string | null;
	created_at: string;
	reports: CreditReport[];
}

interface Props {
	userId: number;
	userNin?: string | null;
}

function formatUGX(v: number | string | null | undefined) {
	if (v == null || v === '') return '—';
	const n = typeof v === 'string' ? parseFloat(v) : v;
	if (Number.isNaN(n)) return '—';
	return `UGX ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function bandColor(band: string) {
	if (!band) return 'default';
	const first = band[0]?.toUpperCase();
	if (first === 'A') return 'green';
	if (first === 'B') return 'lime';
	if (first === 'C') return 'gold';
	if (first === 'D') return 'orange';
	return 'red';
}

function ScoreSparkline({ history }: { history: ScoreHistoryEntry[] }) {
	if (!history?.length) return null;
	const scores = history.map((h) => parseInt(h.Score, 10) || 0).reverse();
	const max = Math.max(...scores, 1);
	const min = Math.min(...scores);
	const W = 240;
	const H = 50;
	const points = scores
		.map((s, i) => {
			const x = (i / Math.max(scores.length - 1, 1)) * W;
			const y = H - ((s - min) / Math.max(max - min, 1)) * H;
			return `${x},${y}`;
		})
		.join(' ');
	const trending = scores[scores.length - 1] >= scores[0];
	return (
		<div>
			<svg width={W} height={H} className="overflow-visible">
				<polyline fill="none" stroke={trending ? '#16a34a' : '#dc2626'} strokeWidth={2} points={points} />
			</svg>
			<Text type="secondary" className="text-xs">
				12-mo score history ({history[history.length - 1]?.MonthYear} → {history[0]?.MonthYear})
			</Text>
		</div>
	);
}

function ReportView({ report, onDownloadPdf, pdfLoading }: { report: CreditReport; onDownloadPdf: (id: number) => void; pdfLoading: boolean }) {
	const info = report.raw_report?.credit_information || {};
	const scoring = info.scoring_details || {};
	const scoreHistory = (Array.isArray(scoring.Score_History) ? scoring.Score_History : []) as ScoreHistoryEntry[];
	const scoreExplanationRaw = scoring.Score_Explanation;
	const scoreExplanation: string[] = Array.isArray(scoreExplanationRaw)
		? (scoreExplanationRaw as string[])
		: scoreExplanationRaw && typeof scoreExplanationRaw === 'object'
			? Object.values(scoreExplanationRaw as Record<string, unknown>).map(String)
			: scoreExplanationRaw
				? [String(scoreExplanationRaw)]
				: [];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const asArr = (v: unknown): any[] => (Array.isArray(v) ? v : []);
	const accountsDetails: AccountDetail[] = asArr(info.credit_account_details);
	const applicationsDetails: ApplicationDetail[] = asArr(info.credit_applications_details);
	const employment: Array<{ Employer_Name?: string; Income_Band?: string }> = asArr(info.employment_history);
	const physicalAddressHistory: Array<{ Period_at_Address?: string }> = asArr(info.physical_address_history);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const applicationsSummary: any[] = asArr(info.credit_applications_summary);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const guarantorSummary: any[] = asArr(info.collateral_guarantor_summary);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const bouncedSummary: any[] = asArr(info.bounced_cheques_summary);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const fraudSummary: any[] = asArr(info.financial_fraud_summary);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const classificationSummary: any[] = asArr(info.credits_accounts_classification_summary);

	const inArrearsAccounts = accountsDetails
		.map((acct) => {
			const last = acct.Last_Transaction?.[0];
			const days = last?.Days_in_Arrears || 0;
			const overdue = parseFloat(acct.Amount_Overdue || last?.Amount_Overdue || '0');
			return { acct, days, overdue };
		})
		.filter((r) => r.days > 0 || r.overdue > 0)
		.sort((a, b) => b.days - a.days);

	const applicationsCounts = applicationsSummary.reduce((acc, block) => {
		Object.entries(block).forEach(([k, v]) => {
			if (Array.isArray(v)) {
				const first = v[0] as { No_of_Applications?: number; Amount?: number } | undefined;
				if (first && typeof first === 'object') {
					acc[k] = { count: first.No_of_Applications || 0, amount: first.Amount || 0 };
				}
			}
		});
		return acc;
	}, {} as Record<string, { count: number; amount: number }>);

	const totalApplications = applicationsSummary.find((b) => 'Total_Credit_Applications' in b)?.Total_Credit_Applications as number | undefined;

	const bouncedCount = (bouncedSummary.find((b) => 'Total_Bounced_Cheques' in b)?.Total_Bounced_Cheques as number) || 0;
	const fraudCount = (fraudSummary.find((b) => 'Total_Financial_Malpractice' in b)?.Total_Financial_Malpractice as number) || 0;

	const guarantorTotal = (guarantorSummary.find((b) => 'Total_Collateral_Guarantors' in b)?.Total_Collateral_Guarantors as number) || 0;
	const guarantorDetail = guarantorSummary.find((b) => 'Collateral_Guarantor' in b) as { Collateral_Guarantor?: Array<{ No_of_Guaranteed_Accounts?: number; Amount_Guaranteed?: number; Balance_on_Guaranteed_Accounts?: string }> } | undefined;
	const guarantorRow = guarantorDetail?.Collateral_Guarantor?.[0];

	const overduePct = report.total_loan_balance && report.total_loan_balance > 0 ? Math.round(((report.total_amount_overdue || 0) / report.total_loan_balance) * 100) : null;

	const employmentInconsistent = employment.length >= 3 && new Set(employment.map((e) => e.Income_Band).filter(Boolean)).size >= 3;
	const addressUnstable = physicalAddressHistory.some((h) => {
		const p = (h.Period_at_Address || '').toLowerCase();
		return p.includes('week') || p.match(/^\d+\s*month/) && parseInt(p, 10) <= 2;
	});

	return (
		<div>
			{(bouncedCount > 0 || fraudCount > 0) && (
				<Alert
					type="error"
					showIcon
					icon={<WarningFilled />}
					className="mb-4"
					message="Fraud / bounced-cheques record detected"
					description={
						<>
							{bouncedCount > 0 && <div>{bouncedCount} bounced cheque(s) on record</div>}
							{fraudCount > 0 && <div>{fraudCount} financial malpractice case(s) on record</div>}
						</>
					}
				/>
			)}

			<Card className="mb-4 shadow-sm">
				<Row gutter={24} align="middle">
					<Col xs={24} md={10}>
						<Text type="secondary">Borrower</Text>
						<Title level={4} style={{ margin: 0 }}>
							{report.customer_name || '—'}
						</Title>
						<Text type="secondary" className="text-xs">
							Individual ID {report.individual_id} · Sector: {report.sector || '—'}
						</Text>
					</Col>
					<Col xs={24} md={7}>
						<Statistic
							title="Credit Score"
							value={report.current_score ?? '—'}
							suffix={
								<div className="ml-2 inline-block">
									{report.score_band && <Tag color={bandColor(report.score_band)}>{report.score_band}</Tag>}
									{report.score_rating && <Tag>{report.score_rating}</Tag>}
								</div>
							}
						/>
						<Text type="secondary" className="text-xs">
							{report.evaluation_period ? `Evaluated: ${report.evaluation_period}` : ''}
						</Text>
					</Col>
					<Col xs={24} md={7}>
						<Text type="secondary">Probability of Default</Text>
						<Title level={4} style={{ margin: 0 }}>
							{report.probability_of_default || '—'}
						</Title>
						<Text type="secondary" className="text-xs">
							{report.likelihood_to_default || ''}
						</Text>
					</Col>
				</Row>
				{scoreHistory.length > 0 && (
					<div className="mt-4">
						<ScoreSparkline history={scoreHistory} />
					</div>
				)}
			</Card>

			<Row gutter={16} className="mb-4">
				<Col xs={12} md={6}>
					<Card size="small"><Statistic title="Open accounts" value={report.open_accounts ?? '—'} /></Card>
				</Col>
				<Col xs={12} md={6}>
					<Card size="small"><Statistic title="Closed accounts" value={report.closed_accounts ?? '—'} /></Card>
				</Col>
				<Col xs={12} md={6}>
					<Card size="small">
						<Statistic
							title="Adverse accounts"
							value={report.adverse_accounts ?? '—'}
							valueStyle={{ color: (report.adverse_accounts || 0) > 0 ? '#dc2626' : undefined }}
						/>
					</Card>
				</Col>
				<Col xs={12} md={6}>
					<Card size="small">
						<Statistic
							title="Worst days in arrears"
							value={report.worst_days_in_arrears ?? '—'}
							valueStyle={{ color: (report.worst_days_in_arrears || 0) > 90 ? '#dc2626' : undefined }}
						/>
					</Card>
				</Col>
			</Row>

			<Row gutter={16} className="mb-4">
				<Col xs={24} md={8}>
					<Card size="small">
						<Statistic title="Total loan balance" value={formatUGX(report.total_loan_balance)} />
					</Card>
				</Col>
				<Col xs={24} md={8}>
					<Card size="small">
						<Statistic
							title="Amount overdue"
							value={formatUGX(report.total_amount_overdue)}
							suffix={overduePct !== null ? <Text type="secondary" className="text-sm">({overduePct}% of bal)</Text> : undefined}
							valueStyle={{ color: (report.total_amount_overdue || 0) > 0 ? '#dc2626' : undefined }}
						/>
					</Card>
				</Col>
				<Col xs={24} md={8}>
					<Card size="small">
						<Statistic
							title="Pending applications"
							value={applicationsCounts.Pending_Applications?.count ?? 0}
							suffix={applicationsCounts.Pending_Applications ? <Text type="secondary" className="text-sm">({formatUGX(applicationsCounts.Pending_Applications.amount)})</Text> : undefined}
						/>
					</Card>
				</Col>
			</Row>

			{(employmentInconsistent || addressUnstable) && (
				<Card size="small" className="mb-4" title={<><AlertOutlined /> Flags</>}>
					<ul className="m-0 pl-4">
						{employmentInconsistent && <li>Income unverifiable — {employment.length} employment entries with inconsistent income bands</li>}
						{addressUnstable && <li>Address history unstable — recent short stays</li>}
					</ul>
				</Card>
			)}

			{guarantorTotal > 0 && guarantorRow && (
				<Card size="small" className="mb-4" title="Guarantor exposure">
					Guarantor on <b>{guarantorRow.No_of_Guaranteed_Accounts}</b> account(s), total{' '}
					<b>{formatUGX(guarantorRow.Amount_Guaranteed)}</b>, balance <b>{formatUGX(guarantorRow.Balance_on_Guaranteed_Accounts)}</b>.
				</Card>
			)}

			{inArrearsAccounts.length > 0 && (
				<Card size="small" className="mb-4" title="Currently in arrears">
					<Table
						size="small"
						rowKey={(r) => `${r.acct.SNo}-${r.acct.Institution}`}
						pagination={false}
						dataSource={inArrearsAccounts}
						columns={[
							{ title: 'Institution', dataIndex: ['acct', 'Institution'] },
							{ title: 'Product', dataIndex: ['acct', 'Credit_Account_Type'] },
							{ title: 'Amount overdue', render: (_, r) => formatUGX(r.overdue) },
							{ title: 'Days in arrears', dataIndex: 'days', render: (v: number) => <Tag color={v > 90 ? 'red' : v > 30 ? 'orange' : 'gold'}>{v} days</Tag> },
						]}
					/>
				</Card>
			)}

			<Collapse
				className="mb-4"
				items={[
					{
						key: 'all-accounts',
						label: `All credit accounts (${accountsDetails.length})`,
						children: (
							<Table
								size="small"
								rowKey={(r) => `${r.SNo}-${r.Institution}`}
								pagination={{ pageSize: 10 }}
								dataSource={[...accountsDetails].sort((a, b) => (b.Last_Transaction?.[0]?.Days_in_Arrears || 0) - (a.Last_Transaction?.[0]?.Days_in_Arrears || 0))}
								columns={[
									{ title: 'Institution', dataIndex: 'Institution' },
									{ title: 'Type', dataIndex: 'Credit_Account_Type' },
									{ title: 'Amount', dataIndex: 'Credit_Amount', render: formatUGX },
									{ title: 'Balance', render: (_, r) => formatUGX(r.Last_Transaction?.[0]?.Current_Balance_Amount) },
									{ title: 'Days arrears', render: (_, r) => r.Last_Transaction?.[0]?.Days_in_Arrears ?? 0 },
									{ title: 'Purpose', dataIndex: 'Loan_Purpose' },
								]}
							/>
						),
					},
					{
						key: 'applications',
						label: `Credit applications (${applicationsDetails.length}${totalApplications ? ` of ${totalApplications} total` : ''})`,
						children: (
							<Table
								size="small"
								rowKey={(r) => `${r.SNo}-${r.Institution}`}
								pagination={{ pageSize: 10 }}
								dataSource={applicationsDetails}
								columns={[
									{ title: 'Institution', dataIndex: 'Institution' },
									{ title: 'Product', dataIndex: 'Product_Type' },
									{ title: 'Amount', dataIndex: 'Amount', render: formatUGX },
									{ title: 'Status', dataIndex: 'Application_Status', render: (v: string) => <Tag>{v}</Tag> },
									{ title: 'Applied', dataIndex: 'Application_Date' },
								]}
							/>
						),
					},
					{
						key: 'classification',
						label: `Exposure by institution type (${classificationSummary.length})`,
						children: (
							<Table
								size="small"
								rowKey={(r, i) => `${r.Institution_Type}-${i}`}
								pagination={false}
								dataSource={classificationSummary as Array<{ Institution_Type?: string; No_of_Accounts?: number; Loan_Amount?: number; Loan_Balance?: string }>}
								columns={[
									{ title: 'Institution type', dataIndex: 'Institution_Type' },
									{ title: '# Accounts', dataIndex: 'No_of_Accounts' },
									{ title: 'Loan amount', dataIndex: 'Loan_Amount', render: formatUGX },
									{ title: 'Loan balance', dataIndex: 'Loan_Balance', render: formatUGX },
								]}
							/>
						),
					},
					{
						key: 'score-explanations',
						label: `Score explanations (${scoreExplanation.length})`,
						children: (
							<ul className="m-0 pl-4">
								{scoreExplanation.map((e, i) => (
									<li key={i}>{e}</li>
								))}
							</ul>
						),
					},
				]}
			/>

			<div className="flex justify-end">
				<Button icon={<DownloadOutlined />} loading={pdfLoading} onClick={() => onDownloadPdf(report.id)}>
					Download PDF (paid)
				</Button>
			</div>
		</div>
	);
}

export const UserCreditReport = ({ userId, userNin }: Props) => {
	const queryClient = useQueryClient();
	const [isRunModalOpen, setIsRunModalOpen] = useState(false);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [reason, setReason] = useState('For device financing');
	const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);
	const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

	const { data: enquiry, isLoading } = useQuery<CreditEnquiry | null>({
		queryKey: ['credit-report', userId],
		queryFn: async () => {
			const res = await axios.get(`/admin/users/${userId}/credit-report`, { withCredentials: true });
			return res.data.results as CreditEnquiry | null;
		},
	});

	const { data: history } = useQuery<CreditEnquiry[]>({
		queryKey: ['credit-report-history', userId],
		queryFn: async () => {
			const res = await axios.get(`/admin/users/${userId}/credit-report/history`, { withCredentials: true });
			return (res.data.results || []) as CreditEnquiry[];
		},
		enabled: isHistoryOpen,
	});

	const runMutation = useMutation({
		mutationFn: async () => {
			const res = await axios.post(`/admin/users/${userId}/credit-report`, { reason }, { withCredentials: true });
			return res.data.results as CreditEnquiry;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['credit-report', userId] });
			queryClient.invalidateQueries({ queryKey: ['credit-report-history', userId] });
			setIsRunModalOpen(false);
			message.success('Credit report fetched');
		},
		onError: handleAPIError,
	});

	const handleDownloadPdf = async (reportId: number) => {
		setPdfLoadingId(reportId);
		try {
			const res = await axios.post(
				`/admin/credit-reports/${reportId}/pdf`,
				{},
				{ withCredentials: true, responseType: 'blob' },
			);
			const blob = new Blob([res.data], { type: 'application/pdf' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `credit-report-${userId}-${reportId}.pdf`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			handleAPIError(err as Error);
		} finally {
			setPdfLoadingId(null);
		}
	};

	const reports = enquiry?.reports || [];

	if (isLoading) {
		return <Card loading className="min-h-[300px]" />;
	}

	return (
		<div>
			<div className="mb-4 flex items-center justify-between">
				<div>
					<Title level={4} style={{ margin: 0 }}>Credit Report</Title>
					<Text type="secondary" className="text-sm">Full CRB credit history from gnuGrid</Text>
				</div>
				<div className="flex gap-2">
					<Button icon={<HistoryOutlined />} onClick={() => setIsHistoryOpen(true)}>History</Button>
					<Button
						type="primary"
						icon={<FileTextOutlined />}
						disabled={!userNin}
						onClick={() => setIsRunModalOpen(true)}
					>
						{enquiry ? 'Run New Report' : 'Run Credit Report'}
					</Button>
				</div>
			</div>

			{!userNin && (
				<Alert type="warning" showIcon message="User has no NIN on record — cannot run credit report." className="mb-4" />
			)}

			{!enquiry ? (
				<Empty description="No credit report on file yet" />
			) : reports.length === 0 ? (
				<Alert type="info" showIcon message={`Enquiry ran but no matches were returned (status: ${enquiry.status})`} />
			) : reports.length === 1 ? (
				<ReportView report={reports[0]} onDownloadPdf={handleDownloadPdf} pdfLoading={pdfLoadingId === reports[0].id} />
			) : (
				<Tabs
					activeKey={activeTab ?? String(reports[0].id)}
					onChange={setActiveTab}
					items={reports.map((r) => ({
						key: String(r.id),
						label: `${r.sector || 'match'} — score ${r.current_score ?? '?'}`,
						children: <ReportView report={r} onDownloadPdf={handleDownloadPdf} pdfLoading={pdfLoadingId === r.id} />,
					}))}
				/>
			)}

			<Modal
				title="Run Credit Report"
				open={isRunModalOpen}
				onCancel={() => setIsRunModalOpen(false)}
				onOk={() => runMutation.mutate()}
				okText="Run"
				okButtonProps={{ loading: runMutation.isPending }}
			>
				<Text type="secondary">
					This will send a paid enquiry to gnuGrid CRB for NIN <b>{userNin}</b> and fetch a full credit report for each match returned.
				</Text>
				<div className="mt-4">
					<Text strong>Reason</Text>
					<Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For device financing" className="mt-1" />
				</div>
			</Modal>

			<Modal
				title="Credit Report History"
				open={isHistoryOpen}
				onCancel={() => setIsHistoryOpen(false)}
				footer={null}
				width={800}
			>
				<Table
					size="small"
					rowKey="id"
					dataSource={history || []}
					pagination={{ pageSize: 10 }}
					columns={[
						{ title: 'Date', dataIndex: 'created_at', render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
						{ title: 'Reason', dataIndex: 'reason' },
						{ title: 'Status', dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
						{ title: 'Matches', dataIndex: 'matches_count' },
						{ title: 'Run by', dataIndex: 'run_by' },
					]}
				/>
			</Modal>
		</div>
	);
};
